import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { defualtWorkscheduleApiLimit } from 'src/app/app/app.constants';
import { ApprovalStatus } from 'src/models';
import { ApiLimitParams, ApiService } from '../api.service';
import { buildMockLeaveApprovalShifts } from '../demo/demo-approvals.mock';
import { buildMockAttendanceShifts } from '../demo/demo-attendance.mock';
import { DemoSeedService } from '../demo/demo-seed.service';
import { DemoService } from '../demo/demo.service';
import { PagingService } from '../paging/paging.service';

export type LeaveType = 'paidLeave' | 'unpaidLeave';

export interface Shift {
  id?: string;
  start: string;
  end: string;
  minimumHours?: number;
  userId?: string;
  leaveType?: LeaveType;
  approved?: ApprovalStatus;
  /** Company-configured leave reason (`leave-reason` GB flag); leave entries only. */
  reason?: string;
  /**
   * Earliest/latest worked time intersecting the shift, stamped server-side by
   * worklog ingestion. Only returned when the request passes `work-range=1`;
   * absent when the user never worked during the shift.
   */
  started?: string;
  ended?: string;
}

/** Tuple sent to `PUT work-schedules`: start, end, minimumHours, leaveType, reason. */
export type ScheduleTuple = [string, string, number?, LeaveType?, string?];

export interface Schedules {
  schedules: ScheduleTuple[];
  userId: string;
  approved?: ApprovalStatus;
}

export interface WorkSchedulesAttendanceIssue {
  issues: number;
  shifts: number;
  currentTagId: string;
  ratio: number;
  currentTagName: string;
}

export interface GetWorkSchedulesParams extends ApiLimitParams {
  user?: string | string[];
  from: string;
  to: string;
  limit?: number;
  timezone?: string;
  'exclude-status'?: string;
  /** Comma-separated leave types. Approvals uses this to load leave requests only. */
  'leave-type'?: string;
  /** When 1, each shift includes its `started`/`ended` work-range stamps. */
  'work-range'?: number;
}

export interface GetAttendanceIssuesOptions extends ApiLimitParams {
  from?: string;
  to?: string;
  sort?: string;
  // API cache time in seconds
  'api-cache'?: number;
}

const defaultPagingLimit = defualtWorkscheduleApiLimit;

/** Backend expects a single `user` query value; comma-separated IDs avoid repeated keys that break `qs` parsing. */
function normalizeWorkSchedulesUserParam(user: GetWorkSchedulesParams['user']): GetWorkSchedulesParams['user'] {
  if (Array.isArray(user) && user.length > 0) {
    return user.join(',');
  }
  return user;
}

@Injectable({ providedIn: 'root' })
export class SchedulesService {
  constructor(
    private api: ApiService,
    private paging: PagingService,
    private demo: DemoService,
    private demoSeed: DemoSeedService,
  ) { }

  get(params: GetWorkSchedulesParams) {
    const limit = params?.limit || defaultPagingLimit;

    if (this.demo.isShowDemoDataActiveSnapshot()) {
      // Attendance loads every variant. Approvals passes `leave-type` and needs
      // approval-shaped leave rows (name, createdAt), not bare attendance shifts.
      const shifts = params['leave-type']
        ? buildMockLeaveApprovalShifts(this.demoSeed, params)
        : buildMockAttendanceShifts(this.demoSeed, params);
      return this.paging.from(shifts, limit);
    }

    const { user, ...rest } = params;
    const query: GetWorkSchedulesParams = {
      ...rest,
      ...(user !== undefined && { user: normalizeWorkSchedulesUserParam(user) }),
    };
    return this.paging.getPagedResult<Shift>(page =>
      this.api.request<{ data: Shift[] }>('get', 'work-schedules', { ...query, page, limit }, null, false, null, 200));
  }

  put(params: Schedules[], query: { from: string, to: string }) {
    return lastValueFrom(this.api.request('put', 'work-schedules', params, { params: query }, true));
  }

  delete(id: string) {
    return lastValueFrom(this.api.request('delete', `work-schedules/${id}`));
  }

  getAttendanceIssues(options: GetAttendanceIssuesOptions) {
    const limit = options?.limit || 200;
    return this.paging.getPagedResult<WorkSchedulesAttendanceIssue>(page =>
      this.api.request('get', 'work-schedules/issues', { ...options, limit, page }), limit);
  }

  editLeaveRequest(id: string, payload: Partial<Schedules>) {
    return lastValueFrom(this.api.request('put', `work-schedules/${id}`, { ...payload, approved: payload.approved }));
  }
}
